import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { Send, Megaphone, Paperclip, X, Clock, Loader2, CalendarDays } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, isSameDay } from "date-fns";
import { vi } from "date-fns/locale";
import { PortalLayout } from "@/components/portal-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useCanMutate } from "@/lib/auth";
import { useEntitlements } from "@/hooks/use-entitlements";
import { openAnnouncementFile } from "@/lib/announcement-utils";
import { UpgradeOverlay } from "@/components/upgrade-overlay";


export const Route = createFileRoute("/admin/announcements")({ component: AnnouncementsPage });

type Audience = "all" | "teachers" | "students" | "class";

const AUDIENCE_LABEL: Record<Audience, string> = {
  all: "Tất cả",
  teachers: "Giáo viên",
  students: "Học sinh",
  class: "Theo lớp",
};

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function AnnouncementsPage() {
  const { user } = useAuth();
  const canMutate = useCanMutate();
  const ent = useEntitlements();
  const qc = useQueryClient();
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [audience, setAudience] = React.useState<Audience>("all");
  const [classId, setClassId] = React.useState<string>("");
  const [file, setFile] = React.useState<File | null>(null);
  const [scheduledAt, setScheduledAt] = React.useState<string>("");
  const [dragOver, setDragOver] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(undefined);
  const [calOpen, setCalOpen] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);


  const minScheduled = React.useMemo(() => {
    const d = new Date(Date.now() + 60_000);
    d.setSeconds(0, 0);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  const classesQ = useQuery({
    queryKey: ["classes-all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("classes").select("id, name").order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const listQ = useQuery({
    queryKey: ["announcements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("announcements")
        .select("id, title, body, audience, class_id, created_at, file_url, file_name, scheduled_at")
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return data ?? [];
    },
  });

  const dateCountMap = React.useMemo(() => {
    const map = new Map<string, number>();
    (listQ.data ?? []).forEach((h: any) => {
      const key = format(new Date(h.created_at), "yyyy-MM-dd");
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return map;
  }, [listQ.data]);

  const datesWithNotifs = React.useMemo(
    () => [...dateCountMap.keys()].map(d => new Date(d)),
    [dateCountMap],
  );

  const filteredHistory = React.useMemo(() => {
    if (!selectedDate) return listQ.data ?? [];
    return (listQ.data ?? []).filter((h: any) => isSameDay(new Date(h.created_at), selectedDate));
  }, [listQ.data, selectedDate]);


  const sendMut = useMutation({
    mutationFn: async () => {
      if (!title.trim() || !body.trim()) throw new Error("Hãy nhập tiêu đề và nội dung");
      if (audience === "class" && !classId) throw new Error("Hãy chọn lớp");
      let scheduledIso: string | null = null;
      if (scheduledAt) {
        const d = new Date(scheduledAt);
        if (d.getTime() <= Date.now()) throw new Error("Thời gian hẹn phải ở tương lai");
        scheduledIso = d.toISOString();
      }

      let fileUrl: string | null = null;
      let fileName: string | null = null;
      if (file) {
        const centerId = (user as { centerId?: string | null } | null)?.centerId;
        if (!centerId) throw new Error("Trung tâm chưa được khởi tạo");
        const ext = file.name.split(".").pop() ?? "bin";
        const path = `${centerId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("announcement-files")
          .upload(path, file, { upsert: false, contentType: file.type || undefined });
        if (upErr) throw new Error("Tải file lên thất bại: " + upErr.message);
        // Bucket is private — store the path only, generate signed URL on display.
        fileUrl = path;
        fileName = file.name;
      }

      const { error } = await supabase.from("announcements").insert({
        title: title.trim(),
        body: body.trim(),
        audience,
        class_id: audience === "class" ? classId : null,
        created_by: user?.id ?? null,
        file_url: fileUrl,
        file_name: fileName,
        scheduled_at: scheduledIso,
        center_id_owner: (user as { centerId?: string | null } | null)?.centerId ?? null,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(scheduledAt ? "Đã lên lịch thông báo" : "Đã gửi thông báo");
      setTitle(""); setBody(""); setAudience("all"); setClassId("");
      setFile(null); setScheduledAt("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["announcements"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("ezyclass:dashboard-refresh"));
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <PortalLayout portal="admin" title="Thông báo hệ thống" breadcrumb={[{ label: "Admin", to: "/admin" }, { label: "Thông báo" }]}>
      {!ent.bulkNotify && !ent.isLoading ? (
        <UpgradeOverlay
          feature="Thông báo hàng loạt"
          description="Gửi thông báo cho tất cả học viên, giáo viên. Hẹn giờ gửi, đính kèm file, chọn đối tượng nhận."
        />
      ) : (
      <div className="grid lg:grid-cols-3 gap-5">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-card border rounded-2xl p-6 shadow-sm"
        >
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2">
            <Megaphone className="size-4" style={{ color: "var(--portal)" }} />
            Soạn thông báo mới
          </h3>
          <Input placeholder="Tiêu đề thông báo..." value={title} onChange={(e) => setTitle(e.target.value)} className="mb-3" />
          <Textarea placeholder="Nội dung thông báo..." rows={6} value={body} onChange={(e) => setBody(e.target.value)} className="mb-4" />

          {/* File upload */}
          <div className="mb-4">
            <Label className="mb-1.5 block">Tệp đính kèm</Label>
            {file ? (
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-2 min-w-0">
                  <Paperclip className="size-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{file.name}</div>
                    <div className="text-xs text-muted-foreground">{formatBytes(file.size)}</div>
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <label
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`block cursor-pointer border-2 border-dashed rounded-lg p-5 text-center text-sm transition-colors ${
                  dragOver ? "border-primary bg-primary/5" : "border-input hover:bg-muted/30"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <Paperclip className="size-5 mx-auto mb-1.5 text-muted-foreground" />
                <div className="text-muted-foreground">📎 Đính kèm file — PDF, Word, Excel, ảnh</div>
                <div className="text-xs text-muted-foreground/70 mt-0.5">Kéo thả hoặc bấm để chọn</div>
              </label>
            )}
          </div>

          {/* Schedule */}
          <div className="mb-4">
            <Label htmlFor="scheduled" className="mb-1.5 block flex items-center gap-1.5">
              <Clock className="size-3.5" /> Hẹn giờ gửi (tuỳ chọn)
            </Label>
            <Input
              id="scheduled"
              type="datetime-local"
              value={scheduledAt}
              min={minScheduled}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
            {scheduledAt && (
              <p className="text-xs text-muted-foreground mt-1">
                📅 Sẽ hiển thị lúc {format(new Date(scheduledAt), "HH:mm 'ngày' dd/MM/yyyy", { locale: vi })}
              </p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Gửi đến</label>
              <Select value={audience} onValueChange={(v) => setAudience(v as Audience)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(AUDIENCE_LABEL) as Audience[]).map(k => (
                    <SelectItem key={k} value={k}>{AUDIENCE_LABEL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {audience === "class" && (
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Lớp</label>
                <Select value={classId} onValueChange={setClassId}>
                  <SelectTrigger><SelectValue placeholder="Chọn lớp" /></SelectTrigger>
                  <SelectContent>
                    {(classesQ.data ?? []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button
              size="sm"
              onClick={() => sendMut.mutate()}
              disabled={!canMutate || sendMut.isPending}
              className="gap-1.5"
              style={{ background: "var(--portal)", color: "white" }}
            >
              {sendMut.isPending ? <><Loader2 className="size-4 animate-spin" />Đang gửi...</> : <><Send className="size-4" />{scheduledAt ? "Lên lịch gửi" : "Gửi thông báo"}</>}
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-card border rounded-2xl p-6 shadow-sm"
        >
          <h3 className="font-display font-semibold mb-4">Lịch sử gửi</h3>

          {/* Calendar filter (compact popover) */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 text-sm font-normal">
                  <CalendarDays className="size-4" />
                  {selectedDate ? format(selectedDate, "dd/MM/yyyy", { locale: vi }) : "Lọc theo ngày"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(day) => {
                    if (day && selectedDate && isSameDay(day, selectedDate)) setSelectedDate(undefined);
                    else setSelectedDate(day);
                    setCalOpen(false);
                  }}
                  modifiers={{ hasNotif: datesWithNotifs }}
                  modifiersClassNames={{ hasNotif: "font-bold relative after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:size-1 after:rounded-full after:bg-[var(--portal)]" }}
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
            {selectedDate && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{filteredHistory.length} thông báo</span>
                <button className="text-blue-600 hover:underline" onClick={() => setSelectedDate(undefined)}>
                  Xem tất cả
                </button>
              </div>
            )}
          </div>

          {listQ.isLoading ? (
            <div className="text-sm text-muted-foreground">Đang tải...</div>
          ) : filteredHistory.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4">
              {selectedDate
                ? `Không có thông báo nào ngày ${format(selectedDate, "dd/MM/yyyy")}`
                : "Chưa có thông báo nào."}
            </div>
          ) : (
            <ul className="space-y-3">
              {filteredHistory.map((h: any) => {
                const isScheduled = h.scheduled_at && new Date(h.scheduled_at).getTime() > Date.now();
                return (
                  <li key={h.id} className="p-3 rounded-lg border hover:bg-muted/30 transition-colors">
                    <div className="text-sm font-medium leading-tight mb-1.5">{h.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2 mb-2">{h.body}</div>
                    {h.file_url && (
                      <button
                        type="button"
                        onClick={() => openAnnouncementFile(h.file_url)}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mb-2"
                      >
                        📎 {h.file_name ?? "Tệp đính kèm"}
                      </button>
                    )}
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{AUDIENCE_LABEL[h.audience as Audience] ?? h.audience}</Badge>
                      {isScheduled && (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">
                          🕐 Hẹn {format(new Date(h.scheduled_at), "HH:mm dd/MM", { locale: vi })}
                        </Badge>
                      )}
                      <span>{format(new Date(h.created_at), "dd/MM/yyyy HH:mm", { locale: vi })}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </motion.div>

      </div>
      )}
    </PortalLayout>
  );
}
